import { useState } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid,
  LineChart, Line, Legend,
} from "recharts";
import {
  Compass, TrendingUp, Sparkles, Plus, Trash2, ArrowLeft, Gauge, Save, Home as HomeIcon, Car,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchSelect } from "@/components/SearchSelect";
import { VerifiedUnavailable } from "@/components/VerifiedUnavailable";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SCENARIO_COLORS = ["#0A84FF", "#34D399", "#F59E0B", "#F87171", "#A855F7", "#22D3EE"];
const VERDICT_STYLE = {
  Conservative: "text-emerald-500 border-emerald-500/40 bg-emerald-500/10",
  Reasonable: "text-sky-500 border-sky-500/40 bg-sky-500/10",
  Aggressive: "text-amber-500 border-amber-500/40 bg-amber-500/10",
  Unrealistic: "text-rose-500 border-rose-500/40 bg-rose-500/10",
};
const fmt = (n) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");
const fmtC = (n) => {
  if (n == null) return "—";
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
};

const DEFAULT_PROFILE = {
  age: 30, location: "", income: 250000, income_growth: 5, career_field: "",
  employment_type: "salary", current_savings: 100000, current_investments: 150000,
  monthly_expenses: 6000, current_debt: 20000, down_payment_capability: 100000,
  savings_rate: 25, risk_tolerance: "moderate", lifestyle_goals: "", horizon_years: 30,
};

const Field = ({ label, children, hint }) => (
  <div>
    <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <p className="text-[10px] text-muted-foreground/60 mt-1">{hint}</p>}
  </div>
);

function ScoreGauge({ score }) {
  const angle = (score / 100) * 180;
  const color = score >= 70 ? "#34D399" : score >= 40 ? "#0A84FF" : score >= 20 ? "#F59E0B" : "#F87171";
  return (
    <div className="flex flex-col items-center" data-testid="affordability-score">
      <div className="relative w-40 h-20 overflow-hidden">
        <div className="absolute w-40 h-40 rounded-full border-[12px] border-border" />
        <div className="absolute w-40 h-40 rounded-full border-[12px] border-transparent"
          style={{ borderTopColor: color, borderRightColor: color, transform: `rotate(${-135 + angle}deg)`, transition: "transform 1s ease" }} />
      </div>
      <div className="font-mono-data text-3xl -mt-2" style={{ color }}>{score}</div>
      <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Affordability Score</div>
    </div>
  );
}

export default function Roadmap({ embedded = false }) {
  const { user } = useAuth();
  const [step, setStep] = useState("form");
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [assets, setAssets] = useState([
    { label: "Waterfront Estate", asset_type: "home", price: 4500000 },
    { label: "Bentley Continental GT", asset_type: "vehicle", price: 275000 },
  ]);
  const [analysis, setAnalysis] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [scenarios, setScenarios] = useState([]);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const num = (v) => (v === "" ? "" : Number(v));

  const setAsset = (i, k, v) => setAssets((a) => a.map((x, j) => (j === i ? { ...x, [k]: k === "price" ? num(v) : v } : x)));
  const addAsset = () => setAssets((a) => [...a, { label: "", asset_type: "vehicle", price: 200000 }]);
  const removeAsset = (i) => setAssets((a) => a.filter((_, j) => j !== i));

  const build = async () => {
    if (!assets.length || assets.some((a) => !a.price)) { toast.error("Add at least one target asset with a price"); return; }
    setLoading(true); setInsights(null);
    try {
      const payload = { ...profile, assets: assets.map((a) => ({ ...a, label: a.label || a.asset_type })) };
      const res = await api.post("/roadmap/analyze", payload);
      setAnalysis(res.data);
      setStep("result");
      fetchInsights(payload, res.data);
    } catch { toast.error("Could not build road map"); }
    finally { setLoading(false); }
  };

  const fetchInsights = async (payload, data) => {
    setAiLoading(true);
    try {
      const res = await api.post("/roadmap/insights", { profile: payload, analysis: data });
      setInsights(res.data.insights);
    } catch { setInsights(null); }
    finally { setAiLoading(false); }
  };

  const saveScenario = () => {
    if (!analysis) return;
    const homes = assets.filter((a) => a.asset_type === "home").length;
    const cars = assets.filter((a) => a.asset_type === "vehicle").length;
    const name = `${fmtC(analysis.summary.total_asset_price)} · age ${profile.age} · ${fmtC(profile.income)} inc · ${homes}H/${cars}V`;
    setScenarios((s) => [...s, {
      name,
      color: SCENARIO_COLORS[s.length % SCENARIO_COLORS.length],
      summary: analysis.summary,
      timeline: analysis.timeline,
    }]);
    toast.success("Scenario captured for comparison");
  };

  const savePlan = async () => {
    if (!user) { toast.error("Sign in to save your road map"); return; }
    try {
      await api.post("/roadmap/plans", { name: `Road Map ${new Date().toLocaleDateString()}`, profile: { ...profile, assets }, analysis });
      toast.success("Road map saved to your dashboard");
    } catch { toast.error("Could not save"); }
  };

  // ---------------- FORM ----------------
  if (step === "form") {
    return (
      <div className={`max-w-[1100px] mx-auto px-5 sm:px-8 ${embedded ? "pt-2 pb-12" : "py-12"}`}>
        {!embedded && (
          <>
            <div className="flex items-center gap-3 mb-2">
              <Compass className="w-6 h-6 text-accent" strokeWidth={1.5} />
              <h1 className="font-display text-4xl tracking-tighter font-medium">Road Map</h1>
            </div>
            <p className="text-muted-foreground mb-6 max-w-2xl">A private wealth-intelligence engine that maps when — and how — you can realistically own the luxury assets you want. Every figure is transparent and explainable.</p>
          </>
        )}
        <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-10 max-w-2xl" data-testid="rm-disclaimer-form">
          <span className="font-mono-data text-[10px] uppercase tracking-wider text-amber-500 mt-0.5">Note</span>
          <p className="text-xs text-amber-600 dark:text-amber-400/90 leading-relaxed">All results are <strong>rough estimates</strong> based on your inputs and generalized assumptions — not financial advice, guarantees, or precise projections. Actual costs, returns, taxes, and timelines will vary. Consult a licensed financial advisor before making decisions.</p>
        </div>

        <div className="space-y-6">
          <section className="bg-surface border border-border rounded-xl p-6">
            <h2 className="font-display text-lg font-medium mb-5">Personal</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              <Field label="Current Age"><Input data-testid="rm-age" type="number" value={profile.age} onChange={(e) => set("age", num(e.target.value))} /></Field>
              <Field label="Location"><Input data-testid="rm-location" value={profile.location} onChange={(e) => set("location", e.target.value)} placeholder="Boston, MA" /></Field>
              <Field label="Annual Income"><Input data-testid="rm-income" type="number" value={profile.income} onChange={(e) => set("income", num(e.target.value))} /></Field>
              <Field label="Income Growth %/yr"><Input data-testid="rm-growth" type="number" value={profile.income_growth} onChange={(e) => set("income_growth", num(e.target.value))} /></Field>
              <Field label="Career Field"><Input data-testid="rm-career" value={profile.career_field} onChange={(e) => set("career_field", e.target.value)} placeholder="Technology" /></Field>
              <Field label="Employment Type">
                <SearchSelect
                  testid="rm-employment"
                  value={profile.employment_type}
                  onChange={(v) => set("employment_type", v)}
                  className="w-full"
                  options={["salary", "business owner", "entrepreneur", "investor", "other"].map((o) => ({ value: o }))}
                />
              </Field>
            </div>
          </section>

          <section className="bg-surface border border-border rounded-xl p-6">
            <h2 className="font-display text-lg font-medium mb-5">Financial Profile</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              <Field label="Current Savings"><Input data-testid="rm-savings" type="number" value={profile.current_savings} onChange={(e) => set("current_savings", num(e.target.value))} /></Field>
              <Field label="Current Investments"><Input data-testid="rm-investments" type="number" value={profile.current_investments} onChange={(e) => set("current_investments", num(e.target.value))} /></Field>
              <Field label="Monthly Expenses"><Input data-testid="rm-expenses" type="number" value={profile.monthly_expenses} onChange={(e) => set("monthly_expenses", num(e.target.value))} /></Field>
              <Field label="Current Debt"><Input data-testid="rm-debt" type="number" value={profile.current_debt} onChange={(e) => set("current_debt", num(e.target.value))} /></Field>
              <Field label="Down Payment Capability"><Input data-testid="rm-downpayment" type="number" value={profile.down_payment_capability} onChange={(e) => set("down_payment_capability", num(e.target.value))} /></Field>
              <Field label="Desired Savings Rate %" hint="% of income invested annually"><Input data-testid="rm-savingsrate" type="number" value={profile.savings_rate} onChange={(e) => set("savings_rate", num(e.target.value))} /></Field>
            </div>
          </section>

          <section className="bg-surface border border-border rounded-xl p-6">
            <h2 className="font-display text-lg font-medium mb-5">Strategy & Lifestyle</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              <Field label="Risk Tolerance" hint="Sets projected investment return">
                <SearchSelect
                  testid="rm-risk"
                  value={profile.risk_tolerance}
                  onChange={(v) => set("risk_tolerance", v)}
                  className="w-full"
                  options={[
                    { value: "conservative", label: "Conservative (4%)" },
                    { value: "moderate", label: "Moderate (7%)" },
                    { value: "aggressive", label: "Aggressive (10%)" },
                  ]}
                />
              </Field>
              <Field label="Planning Horizon (yrs)"><Input data-testid="rm-horizon" type="number" value={profile.horizon_years} onChange={(e) => set("horizon_years", num(e.target.value))} /></Field>
              <Field label="Future Lifestyle Goals"><Input data-testid="rm-goals" value={profile.lifestyle_goals} onChange={(e) => set("lifestyle_goals", e.target.value)} placeholder="Early retirement, family estate…" /></Field>
            </div>
          </section>

          <section className="bg-surface border border-border rounded-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-lg font-medium">Target Luxury Assets</h2>
              <Button variant="outline" size="sm" onClick={addAsset} data-testid="rm-add-asset" className="rounded-full"><Plus className="w-4 h-4 mr-1" />Add</Button>
            </div>
            <div className="space-y-3">
              {assets.map((a, i) => (
                <div key={i} className="grid grid-cols-12 gap-3 items-end" data-testid="rm-asset-row">
                  <div className="col-span-5"><Field label="Asset"><Input value={a.label} onChange={(e) => setAsset(i, "label", e.target.value)} placeholder="e.g. Bentley Continental GT" data-testid={`rm-asset-label-${i}`} /></Field></div>
                  <div className="col-span-3"><Field label="Type">
                    <SearchSelect
                      testid={`rm-asset-type-${i}`}
                      value={a.asset_type}
                      onChange={(v) => setAsset(i, "asset_type", v)}
                      className="w-full"
                      options={[
                        { value: "home", label: "Real Estate" },
                        { value: "vehicle", label: "Vehicle" },
                      ]}
                    />
                  </Field></div>
                  <div className="col-span-3"><Field label="Price"><Input type="number" value={a.price} onChange={(e) => setAsset(i, "price", e.target.value)} data-testid={`rm-asset-price-${i}`} /></Field></div>
                  <div className="col-span-1">{assets.length > 1 && <Button variant="ghost" size="icon" onClick={() => removeAsset(i)} data-testid={`rm-remove-asset-${i}`}><Trash2 className="w-4 h-4" /></Button>}</div>
                </div>
              ))}
            </div>
          </section>

          <div className="flex justify-end">
            <Button onClick={build} disabled={loading} data-testid="rm-build-button" className="rounded-full px-10 h-12">
              {loading ? "Building…" : "Build My Road Map"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- RESULT ----------------
  const s = analysis.summary;
  const chartData = analysis.timeline.map((t) => ({ age: t.age, netWorth: t.net_worth, income: t.income }));

  const compareAges = [...new Set(scenarios.flatMap((sc) => sc.timeline.map((t) => t.age)))].sort((a, b) => a - b);
  const compareData = compareAges.map((age) => {
    const row = { age };
    scenarios.forEach((sc, i) => {
      const pt = sc.timeline.find((t) => t.age === age);
      row[`s${i}`] = pt ? pt.net_worth : null;
    });
    return row;
  });

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10">
      <button onClick={() => setStep("form")} data-testid="rm-edit-button" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Edit inputs
      </button>

      <div className="flex items-start justify-between flex-wrap gap-6 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1"><Compass className="w-6 h-6 text-accent" strokeWidth={1.5} /><h1 className="font-display text-4xl tracking-tighter font-medium">Your Road Map</h1></div>
          <p className="text-muted-foreground">Projection to age {profile.age + profile.horizon_years} · {analysis.assumptions.investment_return_pct}% assumed return · {profile.income_growth}% income growth</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={saveScenario} data-testid="rm-save-scenario" className="rounded-full"><Gauge className="w-4 h-4 mr-2" />Compare</Button>
          <Button onClick={savePlan} data-testid="rm-save-plan" className="rounded-full"><Save className="w-4 h-4 mr-2" />Save</Button>
        </div>
      </div>

      <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-8" data-testid="rm-disclaimer-result">
        <span className="font-mono-data text-[10px] uppercase tracking-wider text-amber-500 mt-0.5">Rough estimates</span>
        <p className="text-xs text-amber-600 dark:text-amber-400/90 leading-relaxed">Every number below is a <strong>rough estimate</strong> derived from your inputs and generalized assumptions (shown at the bottom). These are illustrative projections, not financial advice or guarantees — actual figures will differ. Consult a licensed advisor before acting.</p>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-3 bg-surface border border-border rounded-xl p-6 flex flex-col items-center justify-center">
          <ScoreGauge score={s.affordability_score} />
          <div className="mt-6 w-full space-y-2.5">
            {[["Net worth today", fmtC(s.net_worth_now)], ["Total target value", fmtC(s.total_asset_price)], ["Monthly ownership", fmtC(s.total_monthly_ownership)], ["Income required", fmtC(s.combined_income_required)], ["Purchase window", s.combined_purchase_window_age ? `Age ${s.combined_purchase_window_age}` : "Beyond horizon"]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{k}</span>
                <span className="font-mono-data text-xs">{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-9 bg-surface border border-border rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4" /><h2 className="font-display text-lg font-medium">Net Worth Projection</h2></div>
          <div className="h-[300px] min-h-[300px]" data-testid="rm-projection-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ left: 10, right: 10 }}>
                <defs>
                  <linearGradient id="nw" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--accent))" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="hsl(var(--accent))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="age" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tickFormatter={fmtC} tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" width={60} />
                <Tooltip formatter={(v, n) => [fmt(v), n === "netWorth" ? "Net Worth" : "Income"]} labelFormatter={(a) => `Age ${a}`}
                  contentStyle={{ fontFamily: "JetBrains Mono", fontSize: 12, background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                <ReferenceLine y={s.combined_net_worth_required} stroke="hsl(var(--muted-foreground))" strokeDasharray="4 4"
                  label={{ value: "Net worth needed", fontSize: 10, fill: "hsl(var(--muted-foreground))", position: "insideTopLeft" }} />
                {s.combined_purchase_window_age && <ReferenceLine x={s.combined_purchase_window_age} stroke="hsl(var(--accent))" strokeDasharray="4 4"
                  label={{ value: "Purchase window", fontSize: 10, fill: "hsl(var(--accent))", position: "top" }} />}
                <Area type="monotone" dataKey="netWorth" stroke="hsl(var(--accent))" strokeWidth={2} fill="url(#nw)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Per-asset affordability */}
      <h2 className="font-display text-2xl font-light tracking-tight mt-12 mb-5">Affordability Analysis</h2>
      <div className="grid md:grid-cols-2 gap-6">
        {analysis.assets.map((a, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-surface border border-border rounded-xl p-6" data-testid="rm-asset-analysis">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                {a.asset_type === "home" ? <HomeIcon className="w-5 h-5" strokeWidth={1.5} /> : <Car className="w-5 h-5" strokeWidth={1.5} />}
                <div>
                  <div className="font-editorial text-2xl italic">{a.label}</div>
                  <div className="font-mono-data text-sm text-muted-foreground">{fmt(a.price)}</div>
                </div>
              </div>
              <span className={`font-mono-data text-[10px] uppercase tracking-wider border rounded-full px-3 py-1 ${VERDICT_STYLE[a.verdict]}`} data-testid="rm-verdict">{a.verdict}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[["Income required", fmtC(a.income_required)], ["Down payment", fmtC(a.down_payment)], ["Monthly cost", fmt(a.monthly_cost)], ["Net worth advised", fmtC(a.net_worth_recommended)], ["Purchase window", a.purchase_window_age ? `Age ${a.purchase_window_age}` : "Beyond horizon"], ["10yr opportunity cost", fmtC(a.opportunity_cost_10yr)]].map(([k, v]) => (
                <div key={k} className="metric-empty p-3">
                  <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div>
                  <div className="font-mono-data text-sm mt-1">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-[11px] font-mono-data text-muted-foreground">
              {Object.entries(a.cost_breakdown).map(([k, v]) => (
                <span key={k}>{k.replace(/_/g, " ")}: {fmt(v)}</span>
              ))}
              {a.nw_share_now_pct != null && <span>= {a.nw_share_now_pct}% of today's net worth</span>}
            </div>
          </motion.div>
        ))}
      </div>

      {/* AI insights */}
      <div className="bg-surface border border-border rounded-xl p-6 mt-8">
        <div className="flex items-center gap-2 mb-4"><Sparkles className="w-4 h-4 text-accent" /><h2 className="font-display text-lg font-medium">AI Wealth Strategy</h2><span className="font-mono-data text-[10px] uppercase text-muted-foreground">Interprets your figures only</span></div>
        {aiLoading ? (
          <div className="font-mono-data text-sm text-muted-foreground animate-pulse">Analyzing your projection…</div>
        ) : insights ? (
          <div className="space-y-3" data-testid="rm-ai-insights">
            {insights.split("\n").filter((l) => l.trim()).map((line, i) => (
              <p key={i} className="text-sm leading-relaxed text-foreground/90">{line.replace(/^[-•\d.\s]+/, "")}</p>
            ))}
          </div>
        ) : <VerifiedUnavailable testId="rm-ai-unavailable" note="AI strategy is temporarily unavailable." />}
      </div>

      {/* Milestones */}
      <h2 className="font-display text-2xl font-light tracking-tight mt-12 mb-5">Path to Ownership</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {analysis.milestones.map((m, i) => (
          <div key={i} className="bg-surface border border-border rounded-xl p-5" data-testid="rm-milestone">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Age</div>
            <div className="font-display text-3xl">{m.age}</div>
            <div className="mt-3 space-y-1.5 font-mono-data text-xs">
              <div className="flex justify-between"><span className="text-muted-foreground">Income</span><span>{fmtC(m.income)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Net worth</span><span>{fmtC(m.net_worth)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Savings/yr</span><span>{fmtC(m.annual_savings)}</span></div>
            </div>
            {s.combined_purchase_window_age === m.age && <div className="mt-3 font-mono-data text-[10px] uppercase tracking-wider text-accent">◆ Purchase window</div>}
          </div>
        ))}
      </div>

      {/* Scenario comparison */}
      {scenarios.length > 0 && (
        <div className="mt-12">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-2xl font-light tracking-tight">Scenario Comparison</h2>
            <Button variant="ghost" size="sm" onClick={() => setScenarios([])} data-testid="rm-clear-scenarios" className="rounded-full text-muted-foreground">Clear</Button>
          </div>

          <div className="bg-surface border border-border rounded-xl p-6 mb-6">
            <div className="flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4" /><h3 className="font-display text-lg font-medium">Net Worth Trajectories</h3></div>
            <div className="h-[320px] min-h-[320px]" data-testid="rm-compare-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={compareData} margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="age" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tickFormatter={fmtC} tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" width={60} />
                  <Tooltip formatter={(v, n) => [fmt(v), scenarios[Number(n.replace("s", ""))]?.name || n]} labelFormatter={(a) => `Age ${a}`}
                    contentStyle={{ fontFamily: "JetBrains Mono", fontSize: 11, background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                  <Legend formatter={(val) => <span className="font-mono-data text-[11px] text-muted-foreground">{scenarios[Number(val.replace("s", ""))]?.name || val}</span>} />
                  {scenarios.map((sc, i) => (
                    <Line key={i} type="monotone" dataKey={`s${i}`} name={`s${i}`} stroke={sc.color} strokeWidth={2} dot={false} connectNulls />
                  ))}
                  {scenarios.map((sc, i) => (
                    sc.summary.combined_purchase_window_age ? (
                      <ReferenceLine key={`ref${i}`} x={sc.summary.combined_purchase_window_age} stroke={sc.color} strokeDasharray="3 3" strokeOpacity={0.5} />
                    ) : null
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-muted-foreground/60 mt-2 font-mono-data">Dashed vertical markers indicate each scenario's projected purchase window.</p>
          </div>

          <div className="bg-surface border border-border rounded-xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm" data-testid="rm-scenario-table">
              <thead>
                <tr className="border-b border-border font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left p-4">Scenario</th><th className="text-right p-4">Score</th><th className="text-right p-4">Target Value</th>
                  <th className="text-right p-4">Monthly</th><th className="text-right p-4">Income Req.</th><th className="text-right p-4">Purchase Age</th>
                </tr>
              </thead>
              <tbody>
                {scenarios.map((sc, i) => (
                  <tr key={i} className="border-b border-border last:border-0 font-mono-data">
                    <td className="p-4"><span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ background: sc.color }} />{sc.name}</td>
                    <td className="p-4 text-right">{sc.summary.affordability_score}</td>
                    <td className="p-4 text-right">{fmtC(sc.summary.total_asset_price)}</td>
                    <td className="p-4 text-right">{fmtC(sc.summary.total_monthly_ownership)}</td>
                    <td className="p-4 text-right">{fmtC(sc.summary.combined_income_required)}</td>
                    <td className="p-4 text-right">{sc.summary.combined_purchase_window_age ? `Age ${sc.summary.combined_purchase_window_age}` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground/60 mt-8 font-mono-data leading-relaxed max-w-3xl">{analysis.assumptions.note} Assumptions: {analysis.assumptions.investment_return_pct}% return, {analysis.assumptions.income_growth_pct}% income growth. Home: {Math.round(analysis.assumptions.home.down_payment_pct * 100)}% down, {(analysis.assumptions.home.mortgage_rate * 100).toFixed(2)}% / {analysis.assumptions.home.term_years}yr, ~{(analysis.assumptions.home.property_tax_pct * 100).toFixed(1)}% tax. Vehicle: {Math.round(analysis.assumptions.vehicle.down_payment_pct * 100)}% down, {(analysis.assumptions.vehicle.loan_rate * 100).toFixed(1)}% / {analysis.assumptions.vehicle.term_years}yr.</p>
    </div>
  );
}
